import React, { useEffect, useState } from "react";
import {
  useGetMasterCustomersQuery,
  useGetMasterMeQuery,
  useImpersonateCustomerMutation,
} from "../../services/masterApi";
import { startImpersonation } from "../../helpers/impersonation";
import { toast } from "../../services/showNotification";
import useDebounce from "../../hooks/useDebounce";
import OnboardingHeader from "../../components/OnboardingComponents/OnboardingHeader";
import OnboardingCard from "../../components/OnboardingComponents/OnboardingCard";
import { HeaderSection } from "./HeaderSection";
import { FilterSection } from "./FilterSection";
import { LoadingSkeletons } from "./LoadingSkeletons";
import { CustomerGridView } from "./CustomerGridView";
import { CustomerListView } from "./CustomerListView";
import { PaginationControl } from "./PaginationControl";
import { LogoutModal } from "./LogoutModal";

const Users = () => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedEmailId, setCopiedEmailId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageInput, setPageInput] = useState("1");
  const [searchInput, setSearchInput] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">(
    (localStorage.getItem("usersViewMode") as "grid" | "list") || "grid"
  );

  useEffect(() => {
    localStorage.setItem("usersViewMode", viewMode);
  }, [viewMode]);

  const [testFilter, setTestFilter] = useState<"all" | "test" | "non-test">(
    "all"
  );
  const debounceSearch = useDebounce(searchInput, 400);

  const { data, isLoading, isFetching, isError } = useGetMasterCustomersQuery({
    page,
    limit: pageSize,
    search: debounceSearch,
    isTestUser: testFilter === "all" ? undefined : testFilter === "test",
  });

  const customers = data?.data?.docs ?? [];
  const totalDocs = data?.data?.totalDocs ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const handleLogout = () => {
    setIsLoggingOut(true);
    localStorage.clear();
    window.location.replace("/login");
  };

  const { data: meData, isError: isMeError } = useGetMasterMeQuery();
  const masterUser = meData?.data;

  useEffect(() => {
    if (isMeError) {
      handleLogout();
    }
  }, [isMeError]);

  const [impersonateCustomer] = useImpersonateCustomerMutation();
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setImpersonatingId(null);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleImpersonate = async (customer: any) => {
    if (impersonatingId) return;
    setImpersonatingId(customer._id);
    try {
      const res = await impersonateCustomer(customer._id).unwrap();
      const data = res?.data;
      if (!data?.token) {
        toast.error(
          res?.message ||
            "Either the user doesn't exists on CRM or something went wrong. Please try again."
        );
        setImpersonatingId(null);
        return;
      }
      startImpersonation(data.token, data.user);
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
          "Either the user doesn't exists on CRM or something went wrong. Please try again."
      );
      setImpersonatingId(null);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8F9FA]">
      <OnboardingHeader />

      <div className="mx-auto px-4 py-10 md:px-12">
        <HeaderSection
          masterUser={masterUser}
          onShowLogoutModal={setShowLogoutModal}
        />

        <FilterSection
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          setPage={setPage}
          testFilter={testFilter}
          setTestFilter={setTestFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        {isLoading || isFetching ? (
          <LoadingSkeletons viewMode={viewMode} />
        ) : isError ? (
          <OnboardingCard className="p-12 text-center">
            <p className="font-['Proxima_Nova'] font-medium text-xl text-red-600">
              Failed to load users.
            </p>
          </OnboardingCard>
        ) : customers.length === 0 ? (
          <OnboardingCard className="p-16 text-center">
            <h3 className="font-['Canela_Text'] text-2xl font-bold text-[#030724]">
              No users found
            </h3>
            <p className="mt-3 font-['Proxima_Nova'] text-lg text-[#46464D]">
              Try adjusting your search criteria.
            </p>
          </OnboardingCard>
        ) : (
          <>
            <div className="mb-6">
              <p className="font-['Proxima_Nova'] text-[16px] text-[#46464D]">
                Showing{" "}
                <span className="font-bold text-[#1A1F3C]">
                  {customers.length}
                </span>{" "}
                of <span className="font-bold text-[#1A1F3C]">{totalDocs}</span>{" "}
                users
              </p>
            </div>

            {viewMode === "grid" ? (
              <CustomerGridView
                customers={customers}
                copiedId={copiedId}
                setCopiedId={setCopiedId}
                copiedEmailId={copiedEmailId}
                setCopiedEmailId={setCopiedEmailId}
                impersonatingId={impersonatingId}
                handleImpersonate={handleImpersonate}
              />
            ) : (
              <CustomerListView
                customers={customers}
                copiedEmailId={copiedEmailId}
                setCopiedEmailId={setCopiedEmailId}
                impersonatingId={impersonatingId}
                handleImpersonate={handleImpersonate}
              />
            )}

            <PaginationControl
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              isFetching={isFetching}
              pageInput={pageInput}
              setPageInput={setPageInput}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </>
        )}
      </div>

      <LogoutModal
        showLogoutModal={showLogoutModal}
        setShowLogoutModal={setShowLogoutModal}
        handleLogout={handleLogout}
        isLoading={isLoggingOut}
      />
    </div>
  );
};

export default Users;
